import { useRef, useState, useEffect, useCallback } from 'react';

import type { EditorialContent } from '@cobalt/utilities';
import type { MediaAssetServiceCallback } from '@services/MediaAssetService';
import mediaAssetServiceInstance from '@services/MediaAssetService';
import { MediaAssetServiceStatus } from '@shared-types/mediaAssets.types';
import { IS_SIMULATION } from '@utilities/browser';
import { isEqual } from '@utilities/collection-utils';

const defaultStreamUrl = IS_SIMULATION ? '//content.jwplatform.com/manifests/4vvgI762.m3u8' : undefined;

const getVideoIds = (videos: EditorialContent[]) => videos?.map((v) => v?.id).filter(Boolean) ?? [];

export const useMediaAssetService = (videos: EditorialContent[], checkChangedStreamUrl = false) => {
  const context = useRef(null);
  const inited = useRef(false);
  const savedCheckChangedStreamUrl = useRef(false);
  const savedVideos = useRef<EditorialContent[]>([]);
  const savedResult = useRef<EditorialContent[]>([]);
  const [result, setResult] = useState<EditorialContent[]>([]);

  const setVideoSteam: MediaAssetServiceCallback = (res) => {
    const newResult = savedVideos.current
      .map((video) => {
        const masVideo = res.find((mr) => video.id === mr.mediaAssetId);

        const hlsStreamUrl =
          masVideo?.status === MediaAssetServiceStatus.AVAILABLE ? masVideo.hlsStreamUrl : defaultStreamUrl;
        return {
          ...video,
          hlsStreamUrl,
        } as EditorialContent;
      })
      .filter((v) => v.hlsStreamUrl);

    if (
      savedResult.current.length === newResult.length &&
      (!savedCheckChangedStreamUrl.current || isEqual(savedResult.current, newResult))
    ) {
      return;
    }
    savedResult.current = newResult;
    setResult(newResult);
  };

  const stopPolling = useCallback(() => {
    mediaAssetServiceInstance.off(getVideoIds(savedVideos.current), setVideoSteam, context);
    inited.current = false;
  }, []);

  const startPolling = useCallback(() => {
    if (inited.current || !getVideoIds(savedVideos.current)?.length) return;

    mediaAssetServiceInstance.on(getVideoIds(savedVideos.current), setVideoSteam, context);
    inited.current = true;
  }, []);

  useEffect(() => {
    const videoIds = getVideoIds(videos);
    if (!isEqual(getVideoIds(savedVideos.current), videoIds)) {
      stopPolling();
    }

    savedVideos.current = videos;
    startPolling();
  }, [videos, startPolling, stopPolling]);

  useEffect(() => {
    savedCheckChangedStreamUrl.current = checkChangedStreamUrl;
  }, [checkChangedStreamUrl]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return result;
};

export const useVideoFromMas = (video: EditorialContent, checkChangedStreamUrl = false) =>
  useMediaAssetService([video], checkChangedStreamUrl).at(0);
